// src/pages/Growth.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import { format, startOfDay, subDays, isWithinInterval } from "date-fns";
import {
  FaChartLine,
  FaTasks,
  FaCheckCircle,
  FaFire,
  FaTrophy,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaCalendarCheck,
  FaChevronUp,
  FaChevronDown,
  FaBolt,
  FaStar,
  FaMedal,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Finances from "./Finances";
import GrowthFinances from "./GrowthFinances";
import WorkHourCounter from "../landingPage/WorkHourCounterShowcase";
import WorkHoursAnalytics from "./WorkHoursAnalytics";
import GrowthWorkHours from "./GrowthWorkHours";
import GrowthGoals from "./GrowthGoals";
import { useTranslation } from "react-i18next";
import HabitAnalysis from "./HabitAnalysis";
import GrowthHabitAnalysis from "./GrowthHabitAnalysis";

// Modern gradient background
const PageContainer = styled.div`
  background: var(--primaryBackgroundColor);
  padding: 2rem;
  font-family: "Montserrat", sans-serif;
  transition: 0.5s;

  @media (max-width: 768px) {
    padding: 1rem;
  }
  @media (max-width: 650px) {
    margin-top: 80px;
  }
`;

const MainContainer = styled.div`
  max-width: 2200px;
  margin: 0 auto;
  color: var(--fontPrimaryColor);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  margin-bottom: 3rem;
  position: relative;

  .header-content {
    background: linear-gradient(
      135deg,
      rgba(59, 130, 246, 0.1) 0%,
      rgba(16, 185, 129, 0.05) 100%
    );
    backdrop-filter: blur(10px);
    border-radius: 24px;
    padding: 3rem;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    @media (max-width: 768px) {
      padding: 2rem 1.5rem;
    }
  }

  h1 {
    font-size: 3rem;
    font-weight: 700;
    background: linear-gradient(135deg, #ffffff 0%, #a0aec0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: 768px) {
      font-size: 2.25rem;
    }
  }

  .subtitle {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 600px;
    line-height: 1.6;
  }
`;

const TimePeriodSelector = styled.div`
  display: flex;
  margin-top: 1.5rem;
  gap: 0.75rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  background: var(--secondaryBackgroundColor);
  backdrop-filter: blur(10px);
  padding: 0.75rem;
  border-radius: 16px;
  /* border: 1px solid rgba(255, 255, 255, 0.05); */
  max-width: fit-content;
  color: var(--fontPrimaryColor);
  transition: 0.5s;

  button {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 0.875rem 1.5rem;
    border-radius: 12px;
    transition: 0.5s;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-family: "Montserrat", sans-serif;
    font-size: 0.95rem;
    color: var(--fontPrimaryColor);

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      transform: translateY(-2px);
      border-color: rgba(255, 255, 255, 0.15);
    }

    &.active {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      color: white;
      border-color: transparent;
      box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
    }

    svg {
      font-size: 1.1rem;
    }
    @media (max-width: 450px) {
      font-size: 13px;
      padding: 0.875rem 1.1rem;
    }
  }
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  transition: 0.5s;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: var(--secondaryBackgroundColor);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: 0.5s;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  c &:hover {
    transform: translateY(-8px);
    border-color: ${({ color }) => `${color}40`};
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25),
      0 0 0 1px ${({ color }) => `${color}20`},
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${({ color }) => color} 0%,
      ${({ color }) => `${color}80`} 100%
    );
    border-radius: 20px 20px 0 0;
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    transition: 0.5s;

    .header-left {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .icon-container {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      background: linear-gradient(
        135deg,
        ${({ color }) => color}20 0%,
        ${({ color }) => color}10 100%
      );
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid ${({ color }) => `${color}30`};

      .icon {
        font-size: 1.5rem;
        color: ${({ color }) => color};
      }
    }

    h3 {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--fontPrimaryColor);
      margin: 0;
    }

    .time-period {
      font-size: 0.85rem;
      color: var(--fontPrimaryColor);
      background: rgba(255, 255, 255, 0.05);
      padding: 0.35rem 0.75rem;
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  .card-content {
    .main-value {
      font-size: 3.5rem;
      font-weight: 800;
      margin: 1rem 0;
      color: ${({ color }) => color};
      text-shadow: 0 4px 20px ${({ color }) => `${color}40`};

      @media (max-width: 768px) {
        font-size: 2.75rem;
      }
    }

    .sub-value {
      font-size: 1rem;
      color: var(--fontSecondaryColor);
      margin: 0;
      line-height: 1.5;
    }

    .progress-info {
      margin-top: 1.5rem;
      padding-top: 1rem;
      border-top: 1px solid rgba(255, 255, 255, 0.05);
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.95rem;

      &.positive {
        color: #10b981;
      }

      &.negative {
        color: #ef4444;
      }

      svg {
        font-size: 1.1rem;
      }
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartContainer = styled.div`
  background: var(--secondaryBackgroundColor);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  transition: 0.5s;

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--fontPrimaryColor);
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .view-all {
      color: #3b82f6;
      font-size: 0.95rem;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.3s ease;

      &:hover {
        color: #60a5fa;
        transform: translateX(4px);
      }
    }
  }
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: var(--fontSecondaryColor);

  .loader-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    opacity: 0.5;
    animation: pulse 2s infinite;
  }

  p {
    font-size: 1.1rem;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }
`;

const ErrorMessage = styled.div`
  background: linear-gradient(
    135deg,
    rgba(239, 68, 68, 0.1) 0%,
    rgba(239, 68, 68, 0.05) 100%
  );
  color: #ef4444;
  padding: 1.5rem;
  border-radius: 16px;
  text-align: center;
  margin: 2rem 0;
  border: 1px solid rgba(239, 68, 68, 0.2);
  backdrop-filter: blur(10px);
`;

const DailyBreakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
  color: var(--fontSecondaryColor);

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const DayCard = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  color: var(--fontSecondaryColor);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-4px);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .day-name {
    font-size: 0.85rem;
    color: var(--fontSecondaryColor);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .day-count {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ count }) =>
      count > 0 ? "#10b981" : "var(--fontSecondaryColor)"};
    margin-bottom: 0.25rem;
  }

  .day-label {
    font-size: 0.75rem;
    color: var(--fontSecondaryColor);
  }
`;

const TrendBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.875rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  background: ${({ positive }) =>
    positive ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)"};
  color: ${({ positive }) => (positive ? "#10b981" : "#ef4444")};
  border: 1px solid
    ${({ positive }) =>
      positive ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)"};
`;

// Helper function to safely convert Firestore timestamps
const toDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (val instanceof Date) return val;
  try {
    return new Date(val);
  } catch {
    return null;
  }
};

const Growth = ({ theme, switchTheme }) => {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const [timePeriod, setTimePeriod] = useState("week");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Goal Tracker - ASites Planner";
  }, []);
  // Data states
  const [dashboardTasks, setDashboardTasks] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  const [habits, setHabits] = useState([]);
  const [dashboardProgress, setDashboardProgress] = useState([]);
  const [habitProgress, setHabitProgress] = useState([]);

  // Fetch all data
  useEffect(() => {
    if (!userId) return;

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch dashboard tasks
        const dashboardTasksRef = collection(
          db,
          "users",
          userId,
          "dashboardTasks"
        );
        const dashboardTasksSnapshot = await getDocs(dashboardTasksRef);
        const dashboardTasksData = dashboardTasksSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: toDate(doc.data().createdAt),
          date: toDate(doc.data().date),
          stopRecurrenceDate: toDate(doc.data().stopRecurrenceDate),
        }));
        setDashboardTasks(dashboardTasksData);

        // Fetch client tasks
        const clientsSnapshot = await getDocs(
          collection(db, "users", userId, "clients")
        );
        const clientTasksData = [];

        for (const clientDoc of clientsSnapshot.docs) {
          const clientTasksRef = collection(
            db,
            "users",
            userId,
            "clients",
            clientDoc.id,
            "tasks"
          );
          const clientTasksSnapshot = await getDocs(clientTasksRef);
          clientTasksData.push(
            ...clientTasksSnapshot.docs.map((doc) => ({
              id: doc.id,
              clientId: clientDoc.id,
              ...doc.data(),
              createdAt: toDate(doc.data().createdAt),
              date: toDate(doc.data().date),
            }))
          );
        }
        setClientTasks(clientTasksData);

        // Fetch habits
        const habitsRef = collection(db, "users", userId, "habits");
        const habitsSnapshot = await getDocs(habitsRef);
        const habitsData = habitsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          startDate: toDate(doc.data().startDate),
        }));
        setHabits(habitsData);

        // Fetch dashboard progress
        const progressRef = collection(
          db,
          "users",
          userId,
          "dashboardProgress"
        );
        const progressSnapshot = await getDocs(progressRef);
        const progressData = progressSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: toDate(doc.data().date) || new Date(doc.id + "T00:00:00"),
        }));
        setDashboardProgress(progressData);

        // Fetch habit progress
        const habitProgressRef = collection(
          db,
          "users",
          userId,
          "habitProgress"
        );
        const habitProgressSnapshot = await getDocs(habitProgressRef);
        const habitProgressData = habitProgressSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: toDate(doc.data().date) || new Date(doc.id + "T00:00:00"),
        }));
        setHabitProgress(habitProgressData);
      } catch (err) {
        console.error("Error fetching growth data:", err);
        setError(t("growth.error.loading"));
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();

    // Set up real-time listeners
    const unsubDashboardTasks = onSnapshot(
      collection(db, "users", userId, "dashboardTasks"),
      (snap) => {
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: toDate(doc.data().createdAt),
          date: toDate(doc.data().date),
          stopRecurrenceDate: toDate(doc.data().stopRecurrenceDate),
        }));
        setDashboardTasks(data);
      }
    );

    const unsubHabits = onSnapshot(
      collection(db, "users", userId, "habits"),
      (snap) => {
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          startDate: toDate(doc.data().startDate),
        }));
        setHabits(data);
      }
    );

    const unsubDashboardProgress = onSnapshot(
      collection(db, "users", userId, "dashboardProgress"),
      (snap) => {
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: toDate(doc.data().date) || new Date(doc.id + "T00:00:00"),
        }));
        setDashboardProgress(data);
      }
    );

    const unsubHabitProgress = onSnapshot(
      collection(db, "users", userId, "habitProgress"),
      (snap) => {
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: toDate(doc.data().date) || new Date(doc.id + "T00:00:00"),
        }));
        setHabitProgress(data);
      }
    );

    return () => {
      unsubDashboardTasks();
      unsubHabits();
      unsubDashboardProgress();
      unsubHabitProgress();
    };
  }, [userId, t]);

  const dateKeyToDate = (dateKey) => {
    if (!dateKey || typeof dateKey !== "string") return null;
    const d = new Date(dateKey + "T00:00:00");
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!date) return false;
    return isWithinInterval(startOfDay(date), {
      start: startDate,
      end: endDate,
    });
  };

  const countCompletedDatesInRange = (completedDates, startDate, endDate) => {
    if (!completedDates || typeof completedDates !== "object") return 0;
    let count = 0;
    for (const [dateKey, value] of Object.entries(completedDates)) {
      if (!value) continue;
      const d = dateKeyToDate(dateKey);
      if (d && isWithinInterval(d, { start: startDate, end: endDate })) {
        count += 1;
      }
    }
    return count;
  };

  const countTaskCompletionsInRange = (task, startDate, endDate) => {
    if (!task) return 0;

    // Recurring/sticky tasks track completion per-day in completedDates
    if (task.recurring || task.sticky) {
      return countCompletedDatesInRange(
        task.completedDates,
        startDate,
        endDate
      );
    }

    // One-time tasks: completed is a single boolean; approximate completion day by task.date (preferred) else createdAt
    if (!task.completed) return 0;

    const completionDate = task.date || task.createdAt;
    return isDateInRange(completionDate, startDate, endDate) ? 1 : 0;
  };

  const countHabitCompletionsInRange = (habit, startDate, endDate) => {
    if (!habit) return 0;
    return countCompletedDatesInRange(habit.completedDates, startDate, endDate);
  };

  // Get date range based on selected period
  const getDateRange = () => {
    const endDate = startOfDay(new Date());
    let startDate;

    switch (timePeriod) {
      case "week":
        startDate = subDays(endDate, 6);
        break;
      case "month":
        startDate = subDays(endDate, 29);
        break;
      case "quarter":
        startDate = subDays(endDate, 89);
        break;
      case "year":
        startDate = subDays(endDate, 364);
        break;
      default:
        startDate = subDays(endDate, 6);
    }

    return { startDate: startOfDay(startDate), endDate: startOfDay(endDate) };
  };

  // SIMPLIFIED: Calculate Growth Score (Total completed tasks + habits)
  const calculateGrowthScore = () => {
    const { startDate, endDate } = getDateRange();

    const allTasks = [...dashboardTasks, ...clientTasks];
    const tasksCompleted = allTasks.reduce(
      (sum, task) =>
        sum + countTaskCompletionsInRange(task, startDate, endDate),
      0
    );

    const habitsCompleted = habits.reduce(
      (sum, habit) =>
        sum + countHabitCompletionsInRange(habit, startDate, endDate),
      0
    );

    return tasksCompleted + habitsCompleted;
  };

  // SIMPLIFIED: Calculate Total Tasks Completed
  const calculateTotalTasksCompleted = () => {
    const { startDate, endDate } = getDateRange();

    const allTasks = [...dashboardTasks, ...clientTasks];
    return allTasks.reduce(
      (sum, task) =>
        sum + countTaskCompletionsInRange(task, startDate, endDate),
      0
    );
  };

  // SIMPLIFIED: Calculate Total Habits Completed
  const calculateTotalHabitsCompleted = () => {
    const { startDate, endDate } = getDateRange();

    return habits.reduce(
      (sum, habit) =>
        sum + countHabitCompletionsInRange(habit, startDate, endDate),
      0
    );
  };

  // Calculate Most Productive Day
  const calculateMostProductiveDayStats = () => {
    const { startDate, endDate } = getDateRange();

    const completionsByDateKey = new Map();

    const addIfInRange = (date) => {
      if (!date) return;
      if (!isDateInRange(date, startDate, endDate)) return;
      const key = format(startOfDay(date), "yyyy-MM-dd");
      completionsByDateKey.set(key, (completionsByDateKey.get(key) || 0) + 1);
    };

    const addDateKeyIfInRange = (dateKey) => {
      const d = dateKeyToDate(dateKey);
      if (!d) return;
      if (!isWithinInterval(d, { start: startDate, end: endDate })) return;
      completionsByDateKey.set(
        dateKey,
        (completionsByDateKey.get(dateKey) || 0) + 1
      );
    };

    const allTasks = [...dashboardTasks, ...clientTasks];
    allTasks.forEach((task) => {
      if (task?.completedDates && (task.recurring || task.sticky)) {
        for (const [dateKey, value] of Object.entries(task.completedDates)) {
          if (!value) continue;
          addDateKeyIfInRange(dateKey);
        }
        return;
      }

      // One-time tasks: use task.date (preferred) else createdAt as best-effort
      if (task?.completed) {
        addIfInRange(task.date || task.createdAt);
      }
    });

    habits.forEach((habit) => {
      if (!habit?.completedDates) return;
      for (const [dateKey, value] of Object.entries(habit.completedDates)) {
        if (!value) continue;
        addDateKeyIfInRange(dateKey);
      }
    });

    let bestDateKey = null;
    let bestCount = 0;
    let dailyStats = [];

    for (let i = 0; i <= 6; i++) {
      const date = subDays(endDate, i);
      const key = format(date, "yyyy-MM-dd");
      const count = completionsByDateKey.get(key) || 0;
      dailyStats.unshift({
        key,
        count,
        date,
        dayName: format(date, "EEE"),
      });
    }

    for (const [dateKey, count] of completionsByDateKey.entries()) {
      if (count > bestCount) {
        bestCount = count;
        bestDateKey = dateKey;
      }
    }

    const bestDate = bestDateKey ? dateKeyToDate(bestDateKey) : null;

    return {
      bestDate,
      bestDateKey,
      bestCount,
      dailyStats,
    };
  };

  // Calculate simple percentage change for trend
  const calculateTrend = () => {
    if (timePeriod === "week") return 0;

    const currentScore = calculateGrowthScore();

    // Get previous period score
    const today = startOfDay(new Date());
    let previousStartDate, previousEndDate;

    switch (timePeriod) {
      case "month":
        previousStartDate = subDays(today, 60);
        previousEndDate = subDays(today, 30);
        break;
      case "quarter":
        previousStartDate = subDays(today, 180);
        previousEndDate = subDays(today, 90);
        break;
      case "year":
        previousStartDate = subDays(today, 730);
        previousEndDate = subDays(today, 365);
        break;
      default:
        return 0;
    }

    // Calculate previous period score
    const allTasks = [...dashboardTasks, ...clientTasks];
    const previousTasksCompleted = allTasks.reduce(
      (sum, task) =>
        sum +
        countTaskCompletionsInRange(task, previousStartDate, previousEndDate),
      0
    );

    const previousHabitsCompleted = habits.reduce(
      (sum, habit) =>
        sum +
        countHabitCompletionsInRange(habit, previousStartDate, previousEndDate),
      0
    );

    const previousScore = previousTasksCompleted + previousHabitsCompleted;

    if (previousScore === 0) return currentScore > 0 ? 100 : 0;

    const change = ((currentScore - previousScore) / previousScore) * 100;
    return Math.round(change);
  };

  // Calculate completion rate
  const calculateCompletionRate = () => {
    const growthScore = calculateGrowthScore();
    let maxPossible = 0;

    // Simple estimation - assume at least one task/habit per day is expected
    const { startDate, endDate } = getDateRange();
    const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    maxPossible = days * 2; // At least 1 task and 1 habit per day

    if (maxPossible === 0) return 0;
    return Math.min(Math.round((growthScore / maxPossible) * 100), 100);
  };

  if (loading) {
    return (
      <PageContainer>
        <MainContainer>
          <Loader>
            <FaChartLine className="loader-icon" />
            <p>{t("growth.loading")}</p>
          </Loader>
        </MainContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <MainContainer>
          <ErrorMessage>
            <p>{error}</p>
          </ErrorMessage>
        </MainContainer>
      </PageContainer>
    );
  }

  const growthScore = calculateGrowthScore();
  const totalTasksCompleted = calculateTotalTasksCompleted();
  const totalHabitsCompleted = calculateTotalHabitsCompleted();
  const { bestDate, bestCount, dailyStats } = calculateMostProductiveDayStats();
  const trend = calculateTrend();
  const completionRate = calculateCompletionRate();

  return (
    <>
      <Navbar theme={theme} switchTheme={switchTheme} />
      <PageContainer>
        <MainContainer>
          <Title>{t("growth.title")}</Title>
          <Subtitle>{t("growth.subtitle")}</Subtitle>

          <TimePeriodSelector>
            <button
              className={timePeriod === "week" ? "active" : ""}
              onClick={() => setTimePeriod("week")}
            >
              <FaCalendarDay /> {t("growth.timePeriods.week")}
            </button>
            <button
              className={timePeriod === "month" ? "active" : ""}
              onClick={() => setTimePeriod("month")}
            >
              <FaCalendarWeek /> {t("growth.timePeriods.month")}
            </button>
            <button
              className={timePeriod === "quarter" ? "active" : ""}
              onClick={() => setTimePeriod("quarter")}
            >
              <FaCalendarAlt /> {t("growth.timePeriods.quarter")}
            </button>
            <button
              className={timePeriod === "year" ? "active" : ""}
              onClick={() => setTimePeriod("year")}
            >
              <FaCalendarCheck /> {t("growth.timePeriods.year")}
            </button>
          </TimePeriodSelector>

          <OverviewGrid>
            {/* Box 1: Growth Score */}
            <StatCard color="#3b82f6">
              <div className="card-header">
                <div className="header-left">
                  <div className="icon-container">
                    <FaChartLine className="icon" />
                  </div>
                  <div>
                    <h3>{t("growth.stats.growthScore.title")}</h3>
                    <div className="time-period">
                      {timePeriod === "week"
                        ? t("growth.timePeriods.last7Days")
                        : timePeriod === "month"
                        ? t("growth.timePeriods.last30Days")
                        : timePeriod === "quarter"
                        ? t("growth.timePeriods.last90Days")
                        : t("growth.timePeriods.last365Days")}
                    </div>
                  </div>
                </div>
                <TrendBadge positive={trend >= 0}>
                  {trend >= 0 ? <FaChevronUp /> : <FaChevronDown />}
                  {Math.abs(trend)}%
                </TrendBadge>
              </div>
              <div className="card-content">
                <div className="main-value">{growthScore}</div>
                <p className="sub-value">
                  {t("growth.stats.growthScore.description")}
                </p>
                <div className="progress-info">
                  <FaStar />{" "}
                  <span>
                    {completionRate}% {t("growth.stats.completionRate")}
                  </span>
                </div>
              </div>
            </StatCard>

            {/* Box 2: Total Tasks Completed */}
            <StatCard color="#10b981">
              <div className="card-header">
                <div className="header-left">
                  <div className="icon-container">
                    <FaTasks className="icon" />
                  </div>
                  <div>
                    <h3>{t("growth.stats.tasksCompleted.title")}</h3>
                    <div className="time-period">
                      {timePeriod === "week"
                        ? t("growth.timePeriods.last7Days")
                        : timePeriod === "month"
                        ? t("growth.timePeriods.last30Days")
                        : timePeriod === "quarter"
                        ? t("growth.timePeriods.last90Days")
                        : t("growth.timePeriods.last365Days")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-content">
                <div className="main-value">{totalTasksCompleted}</div>
                <p className="sub-value">
                  {t("growth.stats.tasksCompleted.description")}
                </p>
                <div className="progress-info">
                  <FaBolt />{" "}
                  <span>{t("growth.stats.consistentExecution")}</span>
                </div>
              </div>
            </StatCard>

            {/* Box 3: Total Habits Completed */}
            <StatCard color="#f59e0b">
              <div className="card-header">
                <div className="header-left">
                  <div className="icon-container">
                    <FaCheckCircle className="icon" />
                  </div>
                  <div>
                    <h3>{t("growth.stats.habitsCompleted.title")}</h3>
                    <div className="time-period">
                      {timePeriod === "week"
                        ? t("growth.timePeriods.last7Days")
                        : timePeriod === "month"
                        ? t("growth.timePeriods.last30Days")
                        : timePeriod === "quarter"
                        ? t("growth.timePeriods.last90Days")
                        : t("growth.timePeriods.last365Days")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-content">
                <div className="main-value">{totalHabitsCompleted}</div>
                <p className="sub-value">
                  {t("growth.stats.habitsCompleted.description")}
                </p>
                <div className="progress-info">
                  <FaMedal /> <span>{t("growth.stats.buildingRoutines")}</span>
                </div>
              </div>
            </StatCard>

            {/* Box 4: Most Productive Day */}
            <StatCard color="#ef4444">
              <div className="card-header">
                <div className="header-left">
                  <div className="icon-container">
                    <FaFire className="icon" />
                  </div>
                  <div>
                    <h3>{t("growth.stats.peakPerformance.title")}</h3>
                    <div className="time-period">
                      {t("growth.stats.peakPerformance.subtitle")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-content">
                <div className="main-value">{bestCount}</div>
                <p className="sub-value">
                  {bestDate
                    ? `${t("growth.stats.topDay")}: ${format(
                        bestDate,
                        "MMM d"
                      )}`
                    : t("growth.stats.noCompletions")}
                </p>
                {bestCount > 0 && (
                  <div className="progress-info">
                    <FaTrophy />{" "}
                    <span>{t("growth.stats.recordProductivity")}</span>
                  </div>
                )}
              </div>
            </StatCard>
          </OverviewGrid>

          <StatsGrid>
            <ChartContainer>
              <div className="chart-header">
                <h3>
                  <FaCalendarWeek /> {t("growth.charts.dailyActivity.title")}
                </h3>
              </div>
              <DailyBreakdown>
                {dailyStats.map((day, index) => (
                  <DayCard key={index} count={day.count}>
                    <div className="day-name">
                      {t(`days.${day.dayName.toLowerCase()}Short`)}
                    </div>
                    <div className="day-count">{day.count}</div>
                    <div className="day-label">
                      {t("growth.charts.completions")}
                    </div>
                  </DayCard>
                ))}
              </DailyBreakdown>
            </ChartContainer>

            <ChartContainer>
              <div className="chart-header">
                <h3>
                  <FaChartLine /> {t("growth.charts.performanceInsights.title")}
                </h3>
              </div>
              <div style={{ padding: "1rem 0" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1.5rem",
                    padding: "1rem",
                    background: "var(--thirdBackgroundColor)",
                    borderRadius: "12px",
                  }}
                >
                  <span style={{ color: "var(--fontSecondaryColor)" }}>
                    {t("growth.charts.overallProgress")}
                  </span>
                  <span
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "700",
                      color:
                        completionRate > 70
                          ? "#10b981"
                          : completionRate > 40
                          ? "#f59e0b"
                          : "#ef4444",
                    }}
                  >
                    {completionRate}%
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1.5rem",
                    padding: "1rem",
                    background: "var(--thirdBackgroundColor)",
                    borderRadius: "12px",
                  }}
                >
                  <span style={{ color: "var(--fontSecondaryColor)" }}>
                    {t("growth.charts.activityTrend")}
                  </span>
                  <TrendBadge positive={trend >= 0}>
                    {trend >= 0 ? <FaChevronUp /> : <FaChevronDown />}
                    {Math.abs(trend)}%{" "}
                    {trend >= 0
                      ? t("growth.charts.increase")
                      : t("growth.charts.decrease")}
                  </TrendBadge>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "1rem",
                    background: "var(--thirdBackgroundColor)",
                    borderRadius: "12px",
                  }}
                >
                  <span style={{ color: "var(--fontSecondaryColor)" }}>
                    {t("growth.charts.dailyAverage")}
                  </span>
                  <span
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "700",
                      color: "#3b82f6",
                    }}
                  >
                    {Math.round(
                      growthScore /
                        (timePeriod === "week"
                          ? 7
                          : timePeriod === "month"
                          ? 30
                          : timePeriod === "quarter"
                          ? 90
                          : 365)
                    )}
                  </span>
                </div>
              </div>
            </ChartContainer>
          </StatsGrid>
        </MainContainer>
      </PageContainer>
      <GrowthHabitAnalysis />
      <GrowthGoals />
      <GrowthFinances />
      <GrowthWorkHours />
    </>
  );
};

export default Growth;
